import React, { useState } from 'react'
import { SectionTab } from 'polotno/side-panel'
import { MarketingIcon } from '../hugeIcons/hugeIcons'
import { Input, Button, Upload, Switch, message } from 'antd'
import { UploadOutlined, SwapOutlined } from '@ant-design/icons'
import { uploadFile } from '@src/shared/utils/VideoGeneration/helperFunctions/mediaSearch'
import { useGlobalContext } from '@src/context/globalContext'
import './customCtaPanel.less'

// ─── CTA label configs ────────────────────────────────────────────────────────

const CTA_META: { label: string; badge: string; badgeMod: string }[] = [
  { label: 'No CTA',    badge: 'None',    badgeMod: 'none'   },
  { label: 'Buy Now',   badge: 'Button',  badgeMod: 'button' },
  { label: 'Swipe Up',  badge: 'Swipe',   badgeMod: 'swipe'  },
  { label: 'Click Here',badge: 'Click',   badgeMod: 'click'  },
  { label: 'Slide In',  badge: 'Slide',   badgeMod: 'button' },
  { label: 'Pop In',    badge: 'Pop',     badgeMod: 'button' },
  { label: 'Rotate In', badge: 'Rotate',  badgeMod: 'button' },
]

// ─── Panel ────────────────────────────────────────────────────────────────────

const CtaPanel = () => {
  const {
    ScriptDetails: [scriptDetails, setScriptDetails],
    UserDetail:    [userDetail],
  } = useGlobalContext()

  const brandInfo  = userDetail?.brand_data?.logo || {}
  const defaultImg = brandInfo?.logo || '/images/broll.jpg'
  const ctaConfig  = scriptDetails?.ctaObject || {}

  const initHeadline  = ctaConfig.headline    || 'Be cool, get your cooler now!'
  const initSub       = ctaConfig.subheadline || 'Limited time offer'
  const initCtaText   = ctaConfig.cta         || 'BUY NOW'
  const initPrimary   = ctaConfig.image        || defaultImg
  const initSecondary = ctaConfig.secondaryImage || defaultImg
  const initIdx       = typeof ctaConfig.templateKey === 'number' && ctaConfig.templateKey > 0
    ? ctaConfig.templateKey : 0

  const makeVariations = (
    headline: string, sub: string, ctaText: string,
    primary: string, secondary: string | null,
  ) => [
    { key: 0, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: '',       ctaType: 'none',   ctaIcon: '',  className: 'no-cta'   },
    { key: 1, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: ctaText,  ctaType: 'button', ctaIcon: '',  className: 'buy-now'  },
    { key: 2, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: 'Swipe up ', ctaType: 'swipe', ctaIcon: '↑', className: 'swipe-up' },
    { key: 3, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: 'Click here ', ctaType: 'click', ctaIcon: '👉', className: 'click-here' },
    { key: 4, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: ctaText,  ctaType: 'button', ctaIcon: '',  className: 'slide-in' },
    { key: 5, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: ctaText,  ctaType: 'button', ctaIcon: '',  className: 'pop-in'   },
    { key: 6, image: primary, secondaryImage: secondary, headline, subheadline: sub, cta: ctaText,  ctaType: 'button', ctaIcon: '',  className: 'rotate-in'},
  ]

  const [selectedIdx,       setSelectedIdx]       = useState(initIdx)
  const [headline,          setHeadline]           = useState(initHeadline)
  const [subheadline,       setSubheadline]        = useState(initSub)
  const [ctaText,           setCtaText]            = useState(initCtaText)
  const [primaryImage,      setPrimaryImage]       = useState(initPrimary)
  const [secondaryImage,    setSecondaryImage]     = useState(initSecondary)
  const [useSecondary,      setUseSecondary]       = useState(!!initSecondary)
  const [ctaVariations,     setCtaVariations]      = useState(() =>
    makeVariations(initHeadline, initSub, initCtaText, initPrimary, initSecondary))
  const [uploading,         setUploading]          = useState(false)

  const handleUpload = async (info: any, isPrimary: boolean) => {
    const file =
      info.file?.originFileObj ||
      info.file?.file ||
      info.fileList?.[0]?.originFileObj ||
      info.fileList?.[0]?.file ||
      null
    if (!file) return
    setUploading(true)
    try {
      const url = await uploadFile(file)
      if (url) {
        isPrimary ? setPrimaryImage(url) : setSecondaryImage(url)
        message.success('Image uploaded!')
      } else {
        message.error('Upload failed.')
      }
    } catch {
      message.error('Upload failed.')
    }
    setUploading(false)
  }

  const handleSwap = () => {
    if (!useSecondary) return
    const tmp = primaryImage
    setPrimaryImage(secondaryImage)
    setSecondaryImage(tmp)
  }

  const handleConfirm = () => {
    const secondary = useSecondary ? secondaryImage : null
    setCtaVariations(makeVariations(headline, subheadline, ctaText, primaryImage, secondary))
    setScriptDetails((prev: any) => ({
      ...prev,
      ctaObject: {
        ...prev.ctaObject,
        headline,
        subheadline,
        image: primaryImage,
        secondaryImage: secondary,
        cta: ctaText,
        templateKey: selectedIdx,
        ctaType: ctaVariations[selectedIdx]?.ctaType,
      },
    }))
    message.success('CTA template updated!')
  }

  const selectVariation = (idx: number) => {
    setSelectedIdx(idx)
    setScriptDetails((prev: any) => ({
      ...prev,
      ctaObject: {
        ...prev.ctaObject,
        templateKey: idx,
        ctaType: ctaVariations[idx]?.ctaType,
        cta:     ctaVariations[idx]?.cta,
      },
    }))
  }

  return (
    <div className="cta-panel">

      {/* ── Header ── */}
      <div className="cta-panel__header">
        <p className="cta-panel__title">CTA Templates</p>
        <span className="cta-panel__count">{ctaVariations.length}</span>
      </div>

      {/* ── Form ── */}
      <div className="cta-panel__form">

        {/* Headline */}
        <div className="cta-form__field">
          <label className="cta-form__label">Headline</label>
          <Input
            className="cta-form__input"
            value={headline}
            onChange={e => setHeadline(e.target.value)}
            maxLength={50}
            placeholder="Enter headline…"
          />
        </div>

        {/* Subheadline */}
        <div className="cta-form__field">
          <label className="cta-form__label">Subheadline</label>
          <Input
            className="cta-form__input"
            value={subheadline}
            onChange={e => setSubheadline(e.target.value)}
            maxLength={50}
            placeholder="Enter subheadline…"
          />
        </div>

        {/* CTA Text */}
        <div className="cta-form__field">
          <label className="cta-form__label">CTA Text</label>
          <Input
            className="cta-form__input"
            value={ctaText}
            onChange={e => setCtaText(e.target.value)}
            maxLength={20}
            placeholder="Enter CTA text…"
          />
        </div>

        {/* Primary Image */}
        <div className="cta-form__field">
          <label className="cta-form__label">Primary Image</label>
          <div className="cta-form__image-row">
            <Upload showUploadList={false} beforeUpload={() => false}
              onChange={info => handleUpload(info, true)} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading} size="small"
                className="cta-form__upload-btn">
                Upload
              </Button>
            </Upload>
            {primaryImage && (
              <div className="cta-form__thumb">
                <img src={primaryImage} alt="Primary" />
              </div>
            )}
          </div>
        </div>

        {/* Secondary Image */}
        <div className="cta-form__field">
          <div className="cta-form__image-header">
            <label className="cta-form__label">Secondary Image</label>
            <Switch
              checked={useSecondary}
              onChange={setUseSecondary}
              checkedChildren="On"
              unCheckedChildren="Off"
              size="small"
              className="cta-form__toggle"
            />
          </div>
          {useSecondary && (
            <div className="cta-form__image-row">
              <Upload showUploadList={false} beforeUpload={() => false}
                onChange={info => handleUpload(info, false)} accept="image/*">
                <Button icon={<UploadOutlined />} loading={uploading} size="small"
                  className="cta-form__upload-btn">
                  Upload
                </Button>
              </Upload>
              {secondaryImage && (
                <div className="cta-form__thumb">
                  <img src={secondaryImage} alt="Secondary" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="cta-form__actions">
          {useSecondary && (
            <Button icon={<SwapOutlined />} onClick={handleSwap}
              size="small" className="cta-form__swap-btn">
              Swap Images
            </Button>
          )}
          <Button onClick={handleConfirm} className="cta-form__confirm-btn">
            Confirm
          </Button>
        </div>
      </div>

      {/* ── Preview grid ── */}
      <div className="cta-panel__grid">
        {ctaVariations.map((v, idx) => (
          <div
            key={v.key}
            className={`cta-card ${v.className}${selectedIdx === idx ? ' cta-card--selected' : ''}`}
            onClick={() => selectVariation(idx)}
          >
            {/* Selection tick */}
            {selectedIdx === idx && (
              <span className="cta-card__tick">✓</span>
            )}

            {v.ctaType === 'none' ? (
              <div className="cta-card__none">
                <span>No CTA</span>
              </div>
            ) : (
              <>
                {/* Images */}
                <div className="cta-card__images">
                  <img src={v.image} alt="Primary" className="cta-preview-image" />
                  {useSecondary && v.secondaryImage && (
                    <img src={v.secondaryImage} alt="Secondary" className="cta-preview-image" />
                  )}
                </div>

                {/* Text */}
                <div className="cta-preview-headline">{v.headline}</div>
                <div className="cta-preview-subheadline">{v.subheadline}</div>

                {/* CTA */}
                {v.ctaType === 'button' ? (
                  <button className="cta-preview-btn">{v.cta}</button>
                ) : (
                  <div className="cta-preview-cta-text">
                    {v.cta}<span>{v.ctaIcon}</span>
                  </div>
                )}
              </>
            )}

            {/* Label chip at bottom */}
            <span className={`cta-card__badge cta-card__badge--${CTA_META[idx]?.badgeMod}`}>
              {CTA_META[idx]?.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CtaPanel

export const CustomCtaPanel = {
  name: 'cta-panel',
  Tab: (props: any) => (
    <SectionTab name="CTA" {...props}>
      <MarketingIcon />
    </SectionTab>
  ),
  Panel: CtaPanel,
}
